import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LaravelResourceResponse } from '../../../../../../../../_base-shared/contracts/laravel-response.interface';
import { Affiliate } from '../../../../../../../../_base-shared/models/Affiliate';
import { Case } from '../../../../../../../../_base-shared/models/Case/Case';
import { CaseLoginStatus } from '../../../../../../../../_base-shared/models/Case/CaseLoginStatus';
import { User } from '../../../../../../../../_base-shared/models/User/User';
import { AffiliateService } from '../../../../affiliate/affiliate.service';
import { UserService } from '../../../../user/user.service';
import { CaseService } from '../../../case.service';
import {MatDialog} from '@angular/material/dialog';
import {CaseUserPasswordModalComponent} from '../case-user-password-modal/case-user-password-modal.component';
import { environment } from '../../../../../../environments/environment';
import { MainGlobalEventService } from '../../../../../_shared/services/main-global-event.service';

@Component({
  standalone: false,
  selector:    'app-case-quick-client-editor',
  templateUrl: './case-quick-client-editor.component.html',
  styles:      ['.blue-text{color: #04abe4}'],
})
export class CaseQuickClientEditorComponent implements OnInit {
  @Input() case: Case;
  @Output() caseClientsUpdated: EventEmitter<Case> = new EventEmitter<Case>();
  public loadRelations                             = [
    'lead', 'client.address', 'partner.address', 'linked_by_cases.client.address', 'linked_cases.client.address',
  ];
  public isLoading                                 = 0;
  public form: UntypedFormGroup;
  public serverResponse: LaravelResourceResponse;
  public affiliates: Array<Affiliate>              = [];
  public vulnerabilities: Array<object>            = [];
  public isEditing                                 = false;
  public isSubmitting                              = false;
  public authUser: User;
  public clientLoginStatus: CaseLoginStatus;
  public clientLoginStatusDetails: string;
  public partnerLoginStatus: CaseLoginStatus;
  public partnerLoginStatusDetails: string;

  private validateClientEmailTimeout: any;
  private validatePartnerEmailTimeout: any;

  constructor(
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private translate: TranslateService,
    private caseService: CaseService,
    private affiliateService: AffiliateService,
    private userService: UserService,
    private dialog: MatDialog,
    private globalEventsService: MainGlobalEventService,
  ) {
  }

  ngOnInit(): void {

    this.globalEventsService.authUser$.subscribe(user => {
      this.authUser = user;
      this.fetchAffiliates();
      this.getVulnerabilities();
      this.loadCaseClients(this.case);
    });
  }

  public isElementRestricted(){
    const enabledRoles = environment.ALLOW_CASES_ROLES_IDS;
    const enabledStatusId = environment.BLOCK_CASES_STATUS_IDS;
    const enabledDepartmentsId = environment.ALLOW_CASES_DEPARTMENTS_IDS;

    const isDepartmentAllowed = this.authUser.departments.some(department =>
      enabledDepartmentsId.includes(department.id)
    );

    if (
      !enabledStatusId.includes(this.case.status_id) ||
       enabledRoles.includes(this.authUser.role_id) ||
       isDepartmentAllowed
    ){
      return true;
    }
    return false;
  }

  public toggleEditMode() {
    this.isEditing = !this.isEditing;
    this.toggleFormControls(this.isEditing);
  }

  public submitForm() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const requestData = this.form.value;
    if (!requestData.client.address?.address_1) {
      requestData.client.address.address_1 = null;
    }
    if (this.case.joint_application && !requestData.partner?.address?.address_1) {
      requestData.partner.address.address_1 = null;
    }

    this.isSubmitting = true;
    this.caseService.update(this.case.id, requestData, this.loadRelations)
      .pipe(finalize(() => this.isSubmitting = false))
      .subscribe(
        result => {
          this.toastr.success(
            this.translate.instant('CASES.editor.general.result.update.success'),
            this.translate.instant('SHARED.success'),
          );
          const updatedCase = result.data;
          this.case.lead    = updatedCase.lead;
          this.case.client  = updatedCase.client;
          this.case.partner = updatedCase.partner;
          this.toggleEditMode();
        },
        err => this.serverResponse = err.error,
      );
  }

  private toggleFormControls(isEnabled: boolean) {

    const enabledRoles = environment.ALLOW_CASES_ROLES_IDS;
    const enabledStatusId = environment.BLOCK_CASES_STATUS_IDS;
    const enabledDepartmentsId = environment.ALLOW_CASES_DEPARTMENTS_IDS;

    const isDepartmentAllowed = this.authUser.departments.some(department =>
      enabledDepartmentsId.includes(department.id)
    );

    if (isEnabled) {
      if (
        !enabledStatusId.includes(this.case.status_id) ||
         enabledRoles.includes(this.authUser.role_id) ||
         isDepartmentAllowed
      ){
        //  Client fields
        this.form.get('case.lead.affiliate_id').enable();
        this.form.get('client.first_name').enable();
        this.form.get('client.last_name').enable();
        this.form.get('client.email').enable();
        this.form.get('client.phone').enable();
        this.form.get('client.home_phone').enable();
        this.form.get('client.work_phone').enable();
        this.form.get('client.id_card').enable();
        this.form.get('client.address.address_1').enable();
        this.form.get('client.address.address_2').enable();
        this.form.get('client.vulnerability').enable();

        if (this.case.joint_application) {
          this.form.get('partner.first_name').enable();
          this.form.get('partner.last_name').enable();
          this.form.get('partner.email').enable();
          this.form.get('partner.phone').enable();
          this.form.get('partner.home_phone').enable();
          this.form.get('partner.work_phone').enable();
          this.form.get('partner.id_card').enable();
          this.form.get('partner.debt_aware').enable();
          this.form.get('partner.vulnerability').enable();
          this.form.get('partner.address.address_1').enable();
          this.form.get('partner.address.address_2').enable();
        }
      }
    } else {
      this.form.get('case.lead.affiliate_id').disable();
      this.form.get('client.first_name').disable();
      this.form.get('client.last_name').disable();
      this.form.get('client.email').disable();
      this.form.get('client.phone').disable();
      this.form.get('client.home_phone').disable();
      this.form.get('client.work_phone').disable();
      this.form.get('client.id_card').disable();
      this.form.get('client.address.address_1').disable();
      this.form.get('client.address.address_2').disable();
      this.form.get('client.vulnerability').disable();

      if (this.case.joint_application) {
        this.form.get('partner.first_name').disable();
        this.form.get('partner.last_name').disable();
        this.form.get('partner.email').disable();
        this.form.get('partner.phone').disable();
        this.form.get('partner.home_phone').disable();
        this.form.get('partner.work_phone').disable();
        this.form.get('partner.id_card').disable();
        this.form.get('partner.debt_aware').disable();
        this.form.get('partner.vulnerability').disable();
        this.form.get('partner.address.address_1').disable();
        this.form.get('partner.address.address_2').disable();
      }
    }
  }

  private buildForm(clientCase: Case) {
    this.form = this.fb.group({
      case:    this.fb.group({
        ref_number:        [{value: clientCase.ref_number, disabled: true}],
        joint_application: [clientCase.joint_application, Validators.required],
        lead:              this.fb.group({
          affiliate_id: [{value: clientCase?.lead?.affiliate_id, disabled: true}],
        }),
      }),
      client:  this.fb.group({
        first_name:         [{value: clientCase.client.first_name, disabled: true}, Validators.required],
        last_name:          [{value: clientCase.client.last_name, disabled: true}, Validators.required],
        id_card:            [{value: clientCase.client.id_card, disabled: true}],
        email:              [
          {
            value:    clientCase.client.email,
            disabled: true,
          },
          [Validators.required, Validators.email],
          this.validateClientEmail.bind(this),
        ],
        phone_country_code: ['34', [Validators.required]],
        phone:              [
          {value: clientCase.client.phone, disabled: true},
          [
            Validators.required,
            Validators.maxLength(9),
            Validators.minLength(9),
            Validators.pattern('(6|7|9)([0-9])\\w+'),
          ],
        ],
        home_phone:         [
          {value: clientCase.client.home_phone, disabled: true},
          [
            Validators.maxLength(9),
            Validators.minLength(9),
            Validators.pattern('(6|7|9)([0-9])\\w+'),
          ],
        ],
        work_phone:         [
          {value: clientCase.client.work_phone, disabled: true},
          [
            Validators.maxLength(9),
            Validators.minLength(9),
            Validators.pattern('(6|7|9)([0-9])\\w+'),
          ],
        ],
        address:            this.fb.group({
          address_1: [{value: clientCase.client.address?.address_1, disabled: true}],
          address_2: [{value: clientCase.client.address?.address_2, disabled: true}],
        }),
        vulnerability:      [{value: clientCase.client?.vulnerability, disabled: true}],
      }),
      partner: this.fb.group({
        first_name:            [
          {value: clientCase.partner?.first_name, disabled: true},
          clientCase.joint_application ? [Validators.required] : [],
        ],
        last_name:             [
          {value: clientCase.partner?.last_name, disabled: true},
          clientCase.joint_application ? [Validators.required] : [],
        ],
        id_card:               [{value: clientCase.partner?.id_card, disabled: true}],
        email:                 [
          {
            value:    clientCase.partner?.email,
            disabled: true,
          },
          clientCase.joint_application ? [Validators.required, Validators.email] : [],
          clientCase.joint_application ? [this.validatePartnerEmail.bind(this)] : [],
        ],
        phone_country_code:    ['34', [Validators.required]],
        phone:                 [
          {value: clientCase.partner?.phone, disabled: true},
          [
            Validators.maxLength(9),
            Validators.minLength(9),
            Validators.pattern('(6|7|9)([0-9])\\w+'),
          ],
        ],
        home_phone:         [
          {value: clientCase.partner?.home_phone, disabled: true},
          [
            Validators.maxLength(9),
            Validators.minLength(9),
            Validators.pattern('(6|7|9)([0-9])\\w+'),
          ],
        ],
        work_phone:         [
          {value: clientCase.partner?.work_phone, disabled: true},
          [
            Validators.maxLength(9),
            Validators.minLength(9),
            Validators.pattern('(6|7|9)([0-9])\\w+'),
          ],
        ],
        debt_aware:            [
          {value: clientCase.partner?.debt_aware, disabled: true},
          clientCase.joint_application ? [Validators.required] : [],
        ],
        vulnerability:         [
          {value: clientCase.client?.vulnerability, disabled: true},
        ],
        relation_to_applicant: [
          clientCase.partner?.relation_to_applicant,
          clientCase.joint_application ? [Validators.required] : [],
        ],
        address:               this.fb.group({
          address_1: [{value: clientCase.partner?.address?.address_1 || null, disabled: true}],
          address_2: [{value: clientCase.partner?.address?.address_2 || null, disabled: true}],
        }),
      }),
    });
  }

  private loadCaseClients(clientCase: Case) {
    this.isLoading++;
    this.caseService.get(clientCase.id, this.loadRelations).pipe(finalize(() => this.isLoading--)).subscribe(
      result => {
        this.case.lead            = result.data.lead;
        this.case.client          = result.data.client;
        this.case.partner         = result.data.partner;
        this.case.linked_by_cases = result.data.linked_by_cases;
        this.case.linked_cases    = result.data.linked_cases;
        this.checkClientLoginStatus('client');
        if (this.case.joint_application && this.case.partner) {
          this.checkClientLoginStatus('partner');
        }
        this.buildForm(this.case);
      },
    );
  }

  private fetchAffiliates() {
    this.isLoading++;
    this.affiliateService.index({select_all: 1}).pipe(finalize(() => this.isLoading--))
      .subscribe(result => this.affiliates = result.data);
  }

  private getVulnerabilities() {
    this.vulnerabilities = [
      {label: this.translate.instant('USERS.model.vulnerability.options.depression'), value: 'depression'},
      {
        label: this.translate.instant('USERS.model.vulnerability.options.suicidal-thoughts'),
        value: 'suicidal-thoughts',
      },
      {label: this.translate.instant('USERS.model.vulnerability.options.extreme-poverty'), value: 'extreme-poverty'},
      {
        label: this.translate.instant('USERS.model.vulnerability.options.conflict-behaviour'),
        value: 'conflict-behaviour',
      },
      {label: this.translate.instant('USERS.model.vulnerability.options.health-problems'), value: 'health-problems'},
      {label: this.translate.instant('USERS.model.vulnerability.options.family-loss'), value: 'family-loss'},
      {
        label: this.translate.instant('USERS.model.vulnerability.options.family-history-of-violence'),
        value: 'family-history-of-violence',
      },
    ];
  }

  private validateClientEmail(control: UntypedFormControl): Promise<any> | Observable<any> {
    if (this.validateClientEmailTimeout) {
      clearTimeout(this.validateClientEmailTimeout);
    }
    return new Promise<any>((resolve, reject) => {
      this.validateClientEmailTimeout = setTimeout(() => {
        this.userService.validateField('email', control.value, this.case?.user_id).subscribe(
          result => resolve(null),
          err => resolve({unique: err.error.data?.existing_model}),
        );
      }, 500);
    });
  }

  private validatePartnerEmail(control: UntypedFormControl): Promise<any> | Observable<any> {
    if (this.validatePartnerEmailTimeout) {
      clearTimeout(this.validatePartnerEmailTimeout);
    }
    return new Promise<any>((resolve, reject) => {
      this.validatePartnerEmailTimeout = setTimeout(() => {
        if (!this.case.joint_application) {
          resolve(null);
        }
        this.userService.validateField('email', control.value, this.case?.partner_user_id).subscribe(
          result => resolve(null),
          err => resolve({unique: err.error.data?.existing_model}),
        );
      }, 500);
    });
  }

  public clickToCall(typeOfPhone: string, user: 'client' | 'partner') {
    const phone_number = this.form.get(user).get(typeOfPhone).value;
    this.caseService.call({phone_number}).subscribe((response) => {
    });
  }

  public openUserPasswordModal(userId: number) {
    const dialogRef  = this.dialog.open(CaseUserPasswordModalComponent, {
      width: '20%',
      data:  {
        userId,
        caseId: this.case.id
      },
    });
    //  On close fire handle response
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log(result);
      }
    });
  }

  private checkClientLoginStatus(clientRole: 'client' | 'partner'): void {
    const userId = clientRole === 'partner' ? this.case.partner.id : this.case.client.id;
    this.isLoading++;
    this.caseService.getLoginStatus(this.case.id, userId).pipe(finalize(() => this.isLoading--))
      .subscribe({
        next:  result => {
          if (clientRole === 'client') {
            this.clientLoginStatus        = result.data;
            this.clientLoginStatusDetails = this.returnLoginStatusDetails(this.clientLoginStatus);
          } else {
            this.partnerLoginStatus        = result.data;
            this.partnerLoginStatusDetails = this.returnLoginStatusDetails(this.partnerLoginStatus);
          }
        },
        error: err => console.error(err.errors)
      });
  }

  private returnLoginStatusDetails(loginStatus: CaseLoginStatus): string {
    return 'Login active: ' + (loginStatus.login_active ? this.translate.instant('SHARED.yes') : this.translate.instant('SHARED.no')) + "\n" +
      'Passed Enviar Contrato: ' + (loginStatus.passed_enviar_contrato ? this.translate.instant('SHARED.yes') : this.translate.instant('SHARED.no')) + "\n" +
      'Case active: ' + (loginStatus.case_active ? this.translate.instant('SHARED.yes') : this.translate.instant('SHARED.no')) + "\n" +
      'Case payment active: ' + (loginStatus.case_payment_active ? this.translate.instant('SHARED.yes') : this.translate.instant('SHARED.no')) + "\n" +
      'Has current unpaid fees: ' + (loginStatus.has_current_unpaid_fees ? this.translate.instant('SHARED.yes') : this.translate.instant('SHARED.no')) + "\n";
  }
}
